import React, { useEffect, createContext, useState, useRef } from 'react';
import { connect } from 'react-redux';
import clsx from 'clsx';
import { name as pageName, className } from './__styles__/pageVars.scss';

// components
import { PageWrapper } from 'src/universal/components/PageWrapper';
import ErrorPage from 'src/universal/components/ErrorPage';
import Breadcrumbs from 'src/universal/components/Breadcrumbs';
import { pznPageIds } from 'src/universal/components/PZN/config';
import AttributesTable from './components/ProductAttributesTable/index.js';
import OverviewTable from './components/ProductOverviewTable';
import { useComparePermutationsOfTwo } from '@americanexpress/aqx-components';
import Header from './components/Header';
import CardSelector from './components/CardSelector';
import { FAQ } from 'src/universal/components/FAQ/index.js';
import OfferGatingForm from 'page_modules/utils/offer-gating';
import OgSpinner from 'src/universal/components/OfferGatingSpinner';
import AmazonApplyModal from './components/AmazonApplyModal/index';
import FooterCta from './components/FooterCta';
import TrackPageLoadEvents from './components/TrackPageLoadEvents';
import TrackExperimentImpression from 'src/universal/components/TrackExperimentImpression';

// selectors
import { dispatchPreselectedCardsArrived } from 'src/universal/redux/compareCardsTray';
import { getPznInfo } from 'src/universal/redux/pznData';
import { getOpenFAQ } from './redux';
import { useIsFooterCtaVisible } from './hooks';

// utils
import { InitFetchPageDataHelper } from 'src/universal/redux/utils';
import { dispatchDtmActions } from 'src/universal/components/DispatchDtmActions';

// styles
import FAQstyles from './__styles__/faqStyles.scss?modules';
import breadcrumbStyles from './__styles__/breadcrumbs.scss?modules';
import pgStyles from './__styles__/page.scss?modules';
import './__styles__/navs.scss';

export const CurrentPermutationContext = createContext(null);
export const ToggleModalContext = createContext(null);
export const FooterCtaVisibleContext = createContext(null);
export const OfferImpressionContext = createContext(null);

const FetchPageDataHelper = new InitFetchPageDataHelper({
  pageName
});

const PageComponent = React.memo(function PageComponent({
  dispatch,
  data = {},
  pgPznId,
  setPreselectedCards,
  openFaq,
  pznInfo,
  pznData
}) {
  dispatchDtmActions({
    FetchPageDataHelper,
    dispatch,
    data,
    dtmCategoryPaths: [
      'page.dtm.pageName',
      'page.dtm.subCategory2',
      'page.dtm.subCategory3'
    ],
    usePZN: true,
    pznData
  });

  useEffect(() => {
    setPreselectedCards(data.page.cardList);
  }, []);

  const [isModalOpen, toggleModal] = useState(false);
  const toggle = () => toggleModal(!isModalOpen);
  const footerRef = useRef(null);
  const impressedOffersRef = useRef([]);
  const isFooterCtaVisible = useIsFooterCtaVisible(footerRef);
  const comparePermutationsOfTwoState = useComparePermutationsOfTwo();
  const isBrowser = typeof window === 'object';

  if (isBrowser && pznInfo.hasError) {
    return (
      <ErrorPage
        httpStatus={pznInfo?.errorInfo?.errorCode}
        reqUrl={pznInfo?.errorInfo?.reqUrl}
      />
    );
  }

  useEffect(
    () => {
      if (isModalOpen) {
        window.scrollTo(0, 0);
      }

      //re-setting focus to INav elements when carSelector is present
      setTimeout(() => {
        const targetEle = document.querySelector('a[class*="_logoNavLink_"]');
        if (targetEle && isModalOpen) {
          targetEle.focus();
        }
      }, 100);
    },
    [isModalOpen]
  );

  return (
    <PageWrapper
      displayName={className}
      FetchPageDataHelper={FetchPageDataHelper}
      data={data}
      usePZN={true}
      pznPageId={pgPznId}
      shouldRenderOfferExpiry={true}
    >
      <TrackPageLoadEvents />
      {isModalOpen && <CardSelector toggle={toggle} />}
      <div className={clsx(pgStyles.compareMain, { [pgStyles.hide]: isModalOpen })}>
        <Breadcrumbs containerStyles={breadcrumbStyles} />
        <TrackExperimentImpression experimentName="GCS-186-SBS-AHA" />
        {isBrowser && (
          <div>
            <CurrentPermutationContext.Provider
              value={comparePermutationsOfTwoState}
            >
              <ToggleModalContext.Provider value={toggle}>
                <FooterCtaVisibleContext.Provider value={isFooterCtaVisible}>
                  <OfferImpressionContext.Provider value={impressedOffersRef}>
                    <Header />
                    <OverviewTable />
                    <AttributesTable />
                    <FooterCta footerRef={footerRef} />
                  </OfferImpressionContext.Provider>
                </FooterCtaVisibleContext.Provider>
              </ToggleModalContext.Provider>
            </CurrentPermutationContext.Provider>
          </div>
        )}
      </div>
      <FAQ content={openFaq} styles={FAQstyles} />
      <AmazonApplyModal />
      <OfferGatingForm />
      <OgSpinner />
    </PageWrapper>
  );
});

FetchPageDataHelper.pageDataSetup(PageComponent, className);

const mapStateToProps = state => {
  return {
    data: state[pageName],
    pgPznId: pznPageIds[pageName],
    openFaq: getOpenFAQ(state),
    pznInfo: getPznInfo(state),
    pznData: state.pznData
  };
};

const mapDispatchToProps = dispatch => ({
  dispatch,
  setPreselectedCards: preselectedCards =>
    dispatchPreselectedCardsArrived(dispatch, { preselectedCards })
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PageComponent);
